import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { AdminHeader, AdminSidebar } from "../components/AdminHeader";
import {
  LogOut,
  Users,
  Activity,
  FolderOpen,
  Plus,
  Trash2,
  Edit,
  User,
  Mail,
  Lock,
  Shield,
  ShieldCheck,
  Bell,
  Clock,
  Settings,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";

const PERMISSIONS = [
  { value: "create_projects", label: "Projekte erstellen" },
  { value: "view_all_projects", label: "Alle Projekte sehen" },
  { value: "manage_users", label: "Benutzer verwalten" },
  { value: "reopen_projects", label: "Projekte wieder öffnen" },
];

const AdminUsers = () => {
  const { user, logout, api } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("user");
  
  // Project-specific reminder settings (individual per customer)
  const [reminderSettings, setReminderSettings] = useState({
    enabled: false,
    days_after_invitation: 7,
    max_reminders: 3,
    reminder_interval_days: 7,
  });
  const [isSavingReminders, setIsSavingReminders] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "admin",
    permissions: [],
  });

  const [editUser, setEditUser] = useState({
    name: "",
    role: "admin",
    permissions: [],
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/admin/users");
      setUsers(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post("/admin/users", newUser);
      toast.success("Benutzer erstellt");
      setCreateDialogOpen(false);
      setNewUser({ email: "", password: "", name: "", role: "admin", permissions: [] });
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Erstellen";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setIsSubmitting(true);

    try {
      await api.put(`/admin/users/${selectedUser.id}`, editUser);
      toast.success("Benutzer aktualisiert");
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Aktualisieren";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success("Benutzer gelöscht");
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Löschen";
      toast.error(message);
    }
  };

  const openEditDialog = async (u) => {
    setSelectedUser(u);
    setEditUser({
      name: u.name,
      role: u.role,
      permissions: u.permissions || [],
      is_active: u.is_active !== false,
    });
    setActiveTab("user");
    
    // Load project-specific reminder settings for customers
    if (u.role === "customer" && u.project_id) {
      try {
        const response = await api.get(`/admin/projects/${u.project_id}/reminder-settings`);
        setReminderSettings({
          enabled: response.data.settings?.enabled ?? false,
          days_after_invitation: response.data.settings?.days_after_invitation ?? 7,
          max_reminders: response.data.settings?.max_reminders ?? 3,
          reminder_interval_days: response.data.settings?.reminder_interval_days ?? 7,
        });
      } catch (error) {
        // Default settings for new customers
        setReminderSettings({
          enabled: false,
          days_after_invitation: 7,
          max_reminders: 3,
          reminder_interval_days: 7,
        });
      }
    }
    
    setEditDialogOpen(true);
  };

  const handleSaveReminderSettings = async () => {
    if (!selectedUser?.project_id) return;
    setIsSavingReminders(true);
    
    try {
      // Always save individual settings for this customer
      await api.put(`/admin/projects/${selectedUser.project_id}/reminder-settings`, {
        enabled: reminderSettings.enabled,
        days_after_invitation: reminderSettings.days_after_invitation,
        max_reminders: reminderSettings.max_reminders,
        reminder_interval_days: reminderSettings.reminder_interval_days,
      });
      toast.success("Erinnerungseinstellungen gespeichert");
    } catch (error) {
      toast.error("Fehler beim Speichern der Einstellungen");
    } finally {
      setIsSavingReminders(false);
    }
  };

  const openDeleteDialog = (u) => {
    setSelectedUser(u);
    setDeleteDialogOpen(true);
  };

  const togglePermission = (permission, isNew = false) => {
    if (isNew) {
      setNewUser((prev) => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter((p) => p !== permission)
          : [...prev.permissions, permission],
      }));
    } else {
      setEditUser((prev) => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter((p) => p !== permission)
          : [...prev.permissions, permission],
      }));
    }
  };

  const isSuperAdmin = user?.role === "superadmin";

  const getRoleBadge = (role) => {
    if (role === "superadmin") {
      return (
        <Badge className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800">
          <ShieldCheck className="w-3 h-3 mr-1" />
          Super-Admin
        </Badge>
      );
    }
    if (role === "admin") {
      return (
        <Badge className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
          <Shield className="w-3 h-3 mr-1" />
          Admin
        </Badge>
      );
    }
    return (
      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800">
        <User className="w-3 h-3 mr-1" />
        Kunde
      </Badge>
    );
  };

  return (
    <div className="page-bg min-h-screen">
      <AdminSidebar />
      <div className="ml-56">
        <AdminHeader />

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-manrope font-bold text-2xl text-foreground">
                Benutzerverwaltung
              </h1>
              <p className="text-muted-foreground mt-1">
                Verwalten Sie Admin-Benutzer und deren Berechtigungen
              </p>
            </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white dark:bg-blue-600 dark:hover:bg-blue-700"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Neuer Benutzer
          </Button>
        </div>

        {/* Users Table */}
        <Card className="card-base">
          <CardHeader className="border-b border-border bg-muted/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-muted-foreground" />
              {isSuperAdmin ? "Alle Benutzer" : "Kunden"} ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f] dark:border-blue-400"></div>
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                Keine Benutzer gefunden
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>{isSuperAdmin ? "Berechtigungen / Projekt" : "Projekt"}</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-24">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{getRoleBadge(u.role)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {u.role === "superadmin" ? (
                              <Badge variant="outline" className="text-xs">Alle Rechte</Badge>
                            ) : u.role === "customer" ? (
                              <span className="text-sm text-muted-foreground">{u.project_name || "Kein Projekt"}</span>
                            ) : (
                              <>
                                {(u.permissions || []).map((p) => (
                                  <Badge key={p} variant="outline" className="text-xs">
                                    {PERMISSIONS.find((perm) => perm.value === p)?.label || p}
                                  </Badge>
                                ))}
                                {(!u.permissions || u.permissions.length === 0) && (
                                  <span className="text-muted-foreground text-xs">Keine</span>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={u.is_active !== false 
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" 
                            : "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"}>
                            {u.is_active !== false ? "Aktiv" : "Inaktiv"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {u.role !== "superadmin" && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(u)}
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {(isSuperAdmin || u.role === "customer") && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDeleteDialog(u)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-manrope">Neuen Benutzer erstellen</DialogTitle>
              <DialogDescription>
                Erstellen Sie einen neuen Admin-Benutzer mit Berechtigungen.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="label-style">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Max Mustermann"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-style">E-Mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="benutzer@beispiel.de"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-style">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="password"
                    placeholder="Mindestens 6 Zeichen"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-style">Berechtigungen</Label>
                <div className="space-y-2">
                  {PERMISSIONS.map((perm) => (
                    <div key={perm.value} className="flex items-center space-x-2">
                      <Checkbox
                        id={`new-${perm.value}`}
                        checked={newUser.permissions.includes(perm.value)}
                        onCheckedChange={() => togglePermission(perm.value, true)}
                      />
                      <Label htmlFor={`new-${perm.value}`} className="text-sm cursor-pointer">
                        {perm.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white" disabled={isSubmitting}>
                  {isSubmitting ? "Erstellen..." : "Erstellen"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope">Benutzer bearbeiten</DialogTitle>
              <DialogDescription>
                Bearbeiten Sie {selectedUser?.role === "customer" ? "den Kunden" : "die Berechtigungen für"} {selectedUser?.email}
                {selectedUser?.project_name && (
                  <span className="block mt-1 text-muted-foreground">Projekt: {selectedUser.project_name}</span>
                )}
              </DialogDescription>
            </DialogHeader>
            
            {selectedUser?.role === "customer" ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="user" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Benutzerdaten
                  </TabsTrigger>
                  <TabsTrigger value="reminders" className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Erinnerungen
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="user" className="space-y-4 mt-4">
                  <form onSubmit={handleUpdateUser} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="label-style">Name</Label>
                      <Input
                        value={editUser.name}
                        onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is_active"
                        checked={editUser.is_active}
                        onCheckedChange={(checked) => setEditUser({ ...editUser, is_active: checked })}
                      />
                      <Label htmlFor="is_active" className="text-sm cursor-pointer">
                        Benutzer aktiv
                      </Label>
                    </div>
                    <DialogFooter className="mt-6">
                      <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white dark:bg-blue-600 dark:hover:bg-blue-700" disabled={isSubmitting}>
                        {isSubmitting ? "Speichern..." : "Speichern"}
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>
                
                <TabsContent value="reminders" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Konfigurieren Sie die automatischen Erinnerungen für diesen Kunden individuell.
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="text-base">Erinnerungen aktivieren</Label>
                        <p className="text-sm text-muted-foreground">
                          Automatische E-Mail-Erinnerungen für diesen Kunden senden
                        </p>
                      </div>
                      <Switch
                        checked={reminderSettings.enabled}
                        onCheckedChange={(checked) => setReminderSettings({
                          ...reminderSettings,
                          enabled: checked
                        })}
                        data-testid="project-reminders-enabled"
                      />
                    </div>
                    
                    {reminderSettings.enabled && (
                      <div className="border-t border-border pt-4 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              Tage nach Einladung
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="90"
                              value={reminderSettings.days_after_invitation}
                              onChange={(e) => setReminderSettings({
                                ...reminderSettings,
                                days_after_invitation: parseInt(e.target.value) || 7
                              })}
                              data-testid="project-days-after"
                            />
                            <p className="text-xs text-muted-foreground">
                              Erste Erinnerung nach X Tagen
                            </p>
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              Max. Erinnerungen
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={reminderSettings.max_reminders}
                              onChange={(e) => setReminderSettings({
                                ...reminderSettings,
                                max_reminders: parseInt(e.target.value) || 3
                              })}
                              data-testid="project-max-reminders"
                            />
                            <p className="text-xs text-muted-foreground">
                              Maximale Anzahl
                            </p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            Intervall zwischen Erinnerungen (Tage)
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            max="30"
                            value={reminderSettings.reminder_interval_days}
                            onChange={(e) => setReminderSettings({
                              ...reminderSettings,
                              reminder_interval_days: parseInt(e.target.value) || 7
                            })}
                            className="max-w-[200px]"
                            data-testid="project-interval"
                          />
                        </div>
                        
                        {/* Summary */}
                        <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                          <p className="text-sm text-blue-800 dark:text-blue-200">
                            <strong>Zusammenfassung:</strong> Nach {reminderSettings.days_after_invitation} Tagen 
                            wird die erste Erinnerung gesendet. Danach alle {reminderSettings.reminder_interval_days} Tage, 
                            maximal {reminderSettings.max_reminders} mal.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <DialogFooter className="mt-6">
                      <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button 
                        type="button" 
                        onClick={handleSaveReminderSettings}
                        className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white dark:bg-blue-600 dark:hover:bg-blue-700" 
                        disabled={isSavingReminders}
                        data-testid="save-project-reminders"
                      >
                        {isSavingReminders ? "Speichern..." : "Speichern"}
                      </Button>
                    </DialogFooter>
                  </div>
                </TabsContent>
              </Tabs>
            ) : (
              <form onSubmit={handleUpdateUser} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="label-style">Name</Label>
                  <Input
                    value={editUser.name}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                    required
                  />
                </div>
                {selectedUser?.role === "admin" && isSuperAdmin && (
                  <div className="space-y-2">
                    <Label className="label-style">Berechtigungen</Label>
                    <div className="space-y-2">
                      {PERMISSIONS.map((perm) => (
                        <div key={perm.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-${perm.value}`}
                            checked={editUser.permissions.includes(perm.value)}
                            onCheckedChange={() => togglePermission(perm.value, false)}
                          />
                          <Label htmlFor={`edit-${perm.value}`} className="text-sm cursor-pointer">
                            {perm.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={editUser.is_active}
                    onCheckedChange={(checked) => setEditUser({ ...editUser, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="text-sm cursor-pointer">
                    Benutzer aktiv
                  </Label>
                </div>
                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white dark:bg-blue-600 dark:hover:bg-blue-700" disabled={isSubmitting}>
                    {isSubmitting ? "Speichern..." : "Speichern"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Benutzer löschen</DialogTitle>
              <DialogDescription>
                Sind Sie sicher, dass Sie den Benutzer "{selectedUser?.email}" löschen möchten?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white">
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      </div>
    </div>
  );
};

export default AdminUsers;
